// « ✍️ Légendes IA » — staff only (owner, 2026-10-06 — #WA-269649).
//
// The client asked for a Spanish and an Arabic line on each of 46 photos; a team
// member wrote them by hand in 25 minutes. Here: choose the client's languages
// (in her order), give the facts she gave (a first name, a date), and the vision
// model writes one line per language for every photo. NOTHING is saved until
// « Appliquer »: every caption is shown first and can be changed or cleared.
// Applying is a normal edit — autosave, Ctrl+Z, history.
//
// CAPTIONS × MODELS (owner, 2026-10-14 — « match the models we have … so it
// looks pretty, not random text »): on a page laid out with a model, a photo's
// words go INTO the model's own text box for it (its place, font, colour) and
// the model is asked for words that fit that box. A photo the model gave no
// box (owner, 2026-10-14) gets a FREE caption on the paper next to it — under,
// above or beside it, in the model's caption font and ink, one size for the
// album, never within 12 mm of an edge or 15 mm of the spine; no room there:
// on the photo (off faces); no room at all: « Pas de place ». The page's
// « Lieu · date » takes the client's place and date.
//
// REWORK (owner, 2026-10-15):
//   · « Une légende pour la page »: the model sees ALL the photos of the page
//     and writes ONE caption, which REPLACES the model's Sous-titre or Section
//     (the theme's words come back with « Retirer ») — or, on a page with no
//     such box, goes with its first photo. Per page, or for every page at once.
//   · « Tout retirer » for the whole range, not page by page.
//   · « Police » shows every font DRAWN, the model's own first (CaptionFontPicker).
//   · While the model works: a moving bar, the elapsed seconds, and the pages
//     being written shimmer.
//
// Placement is the shared rule (lib/captionLayout.ts): the top or bottom band of
// the photo, never over a face or another text, inside the print safe zone, one
// size for the album, ink read from the photo, NEVER a band or veil over the
// picture — and on a model page, the model's own text box.
import { useEffect, useMemo, useState } from 'react';
import {
  BAND_DEFAULT, captionTargets, existingCaptions, existingPageCaptions, freeLook, pageCaptionBoxes, pageCaptionCapacity,
  placeHolderCount, placeLineOf, PAGE_ROLE_FR,
  type CaptionPage, type CaptionPhoto, type CaptionStyle, type CaptionTarget, type PageCaptionBox,
} from '@/lib/captionLayout';
import { captionsApi, type CaptionLangCode, type CaptionPreviewResult } from '@/lib/captionsApi';
import { captionPhotoIds, draftToAlbum, frenchReason, lumaGridOf, photoMetaFrom, samplerFrom, type LumaGrid } from '@/lib/editorCaptions';
import { CaptionFontPicker } from './CaptionFontPicker';
import { fontName } from '@/lib/editorFonts';

const LANGS: Array<{ code: CaptionLangCode; label: string }> = [
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'darija', label: 'Darija (latin)' },
];

/** The inks offered next to « Couleur du modèle » (plus any colour from the picker). */
const INKS = ['#1F1A1C', '#FFFFFF', '#5B2F3F', '#8B6F26', '#2E4A62', '#7A2E2E', '#3F5E3A'];
const ROLE_FR: Record<string, string> = { legende: 'Légende', mot_photo: 'Mot sous photo', mot_manuscrit: 'Mot manuscrit', sous_titre: 'Sous-titre', section: 'Section' };
/** Where a free caption sits next to its photo. */
const SPOT_FR: Record<string, string> = { below: 'sous la photo', above: 'au-dessus de la photo', left: 'à gauche de la photo', right: 'à droite de la photo' };

// The editor's chrome tokens (night mode for staff) — see AlbumEditorPage `UI`.
const T = {
  ink: 'var(--color-ed-ink)', muted: 'var(--color-adm-muted)', surface: 'var(--color-adm-surface)',
  panel: 'var(--color-ed-panel)', line: 'var(--color-adm-line)', goldInk: 'var(--color-adm-gold-ink)',
  danger: 'var(--color-ed-danger)', chip: 'var(--color-adm-surface-3)', ok: 'var(--color-ed-ok, #3F8F5A)',
};
const GOLD = '#C9A24A';
const GOLD_BTN = `linear-gradient(135deg, ${GOLD}, #8B6F26)`;

// The loading moment: a bar that travels, and the pages being written shimmer. Still when reduced motion is asked.
const MOTION_CSS = `
@keyframes capbar { 0% { transform: translateX(-100%); } 100% { transform: translateX(250%); } }
@keyframes capshimmer { 0% { background-position: -320px 0; } 100% { background-position: 320px 0; } }
@keyframes capspin { to { transform: rotate(360deg); } }
.cap-bar { animation: capbar 1.4s ease-in-out infinite; }
.cap-shimmer { background-image: linear-gradient(90deg, transparent 0, rgba(201,162,74,0.22) 50%, transparent 100%); background-size: 320px 100%; background-repeat: no-repeat; animation: capshimmer 1.2s linear infinite; }
.cap-spin { animation: capspin 0.8s linear infinite; }
@media (prefers-reduced-motion: reduce) { .cap-bar, .cap-shimmer, .cap-spin { animation: none; } }
`;

export interface AiCaptionsPhoto {
  id: string;
  width?: number | null; height?: number | null; face_data?: unknown;
  thumbnail_signed_url?: string | null; signed_url?: string | null;
}

/** What one target means, in French, before anything is written. */
function targetHint(t: CaptionTarget | undefined, themed: boolean): string {
  if (!t) return '';
  if (t.kind === 'holder') return `→ cadre « ${ROLE_FR[t.role] ?? t.role} » du modèle · ${t.maxChars} car. max par langue${t.mode === 'inline' ? ' (langues sur une ligne)' : ''}`;
  if (t.kind === 'free' && themed && t.where === 'paper') return `→ texte libre ${SPOT_FR[t.spot ?? 'below']} · ${t.maxChars} car. max par langue`;
  if (t.kind === 'free') return themed ? '→ sur la photo (pas de place libre autour d’elle sur la page)' : '→ sur la photo';
  return `${t.reason === 'no_holder' ? '—' : '✕'} ${frenchReason(t.reason, t.why)}`;
}

function Spinner({ size = 14 }: { size?: number }) {
  return (
    <svg className="cap-spin shrink-0" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** A setting that is on or off: a switch, its name, and one quiet line saying what it does. */
function Toggle({ on, onChange, label, help, children }: { on: boolean; onChange: (v: boolean) => void; label: string; help?: string; children?: React.ReactNode }) {
  return (
    // ⚠️ `relative` (owner, 2026-10-17): the sr-only checkbox is position:absolute.
    // Without a positioned label it sat at its static spot low in the window; its
    // focus scrolled the WHOLE window up — the bar jumped and a blank band appeared.
    <label className="relative flex items-start gap-2.5 cursor-pointer select-none">
      <input type="checkbox" className="sr-only peer" checked={on} onChange={(e) => onChange(e.target.checked)} />
      <span
        aria-hidden
        className="relative mt-0.5 shrink-0 w-8 h-[18px] rounded-full transition-colors peer-focus-visible:outline peer-focus-visible:outline-2"
        style={{ background: on ? GOLD : T.chip, border: `1px solid ${on ? GOLD : T.line}`, outlineColor: GOLD }}
      >
        <span className="absolute top-[1px] w-[14px] h-[14px] rounded-full bg-white shadow transition-[left]" style={{ left: on ? 15 : 1 }} />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[13px] leading-tight" style={{ color: T.ink }}>{label}{children}</span>
        {help && <span className="block text-[11px] leading-snug mt-0.5" style={{ color: T.muted }}>{help}</span>}
      </span>
    </label>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[13px] font-semibold" style={{ color: T.ink }}>
        {title}{hint && <span className="font-normal" style={{ color: T.muted }}> · {hint}</span>}
      </h3>
      {children}
    </section>
  );
}

export function AiCaptionsDialog(props: {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  /** The album as the editor has it, each page's framing folded in (withSlotPositions). */
  pages: CaptionPage[];
  photos: AiCaptionsPhoto[];
  gapMm: number;
  /** The book's band (its cover spine colours — captionLayout.bandStyleFrom): Roaa's pink frame. */
  bandStyle?: CaptionStyle;
  /** « Appliquer » — the album with the captions placed (only `elements` changed). */
  onApply: (placed: CaptionPage[], counts: { placed: number; skipped: number; removed: number }) => void;
}) {
  const { open, onClose, sessionId, pages, photos, gapMm, onApply } = props;
  const bandStyle = props.bandStyle ?? BAND_DEFAULT;
  // Roaa's book (owner, 2026-10-06): a quote under each line, on a band of the book colour.
  const [quote, setQuote] = useState(false);
  // OFF every time the window opens (owner, 2026-10-17) — never remembered.
  const [band, setBand] = useState(false);
  const [langs, setLangs] = useState<CaptionLangCode[]>([]);
  const [allPages, setAllPages] = useState(true);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(Math.max(1, pages.length));
  const [factsText, setFactsText] = useState('');
  // « Consignes du client » (owner, 2026-10-15): names, dates, exact phrases, which photo gets what —
  // kept for this album while the tab lives (a reopened dialog still has them).
  const wishKey = `storiz:caption-wishes:${sessionId}`;
  const [wishes, setWishesState] = useState(() => { try { return sessionStorage.getItem(wishKey) ?? ''; } catch { return ''; } });
  const setWishes = (v: string) => { setWishesState(v); try { sessionStorage.setItem(wishKey, v); } catch { /* private mode */ } };
  const [replaceHand, setReplaceHand] = useState(false);
  // A model page's photo with no text box of its own: a free caption next to it (owner, 2026-10-14).
  const [themedFree, setThemedFree] = useState(true);
  // '' = the model's own (a holder's design font / ink; the album's caption face on a photo).
  const [font, setFont] = useState('');
  const [ink, setInk] = useState('');
  const before = useMemo(() => existingCaptions(pages), [pages]);
  const pageBefore = useMemo(() => existingPageCaptions(pages), [pages]);
  const [placeLine, setPlaceLine] = useState(() => placeLineOf(pages));
  // Pages being written now ('all' = the whole range).
  const [busy, setBusy] = useState<{ pages: number[]; all: boolean; at: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CaptionPreviewResult | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>(() => ({ ...before }));
  // « Une légende pour la page »: page → its box (null = with its first photo).
  const [pageMode, setPageMode] = useState<Record<number, string | null>>(() => Object.fromEntries(Object.entries(pageBefore).map(([n, v]) => [Number(n), v.elementId])));
  const [pageTexts, setPageTexts] = useState<Record<number, string>>(() => Object.fromEntries(Object.entries(pageBefore).map(([n, v]) => [Number(n), v.text])));
  const [pageFailed, setPageFailed] = useState<Record<number, string>>({});
  // The model has answered at least once (the « N légendes écrites » line).
  const [ran, setRan] = useState(false);
  const [removed, setRemoved] = useState<number[]>([]);
  // The words a « Retirer » took off, page by page — « Remettre » puts back exactly these.
  const [stash, setStash] = useState<Record<number, { texts: Record<string, string>; page: string }>>({});
  const [grids, setGrids] = useState<Record<string, LumaGrid>>({});

  const photoById = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);
  const meta = useMemo<Record<string, CaptionPhoto>>(() => {
    const m = photoMetaFrom(photos);
    // The worker's copy wins when it has one (same columns, read at generation time).
    for (const [id, x] of Object.entries(result?.meta ?? {})) if (x.width > 0 && x.height > 0) m[id] = { id, width: x.width, height: x.height, faces: x.faces ?? [] };
    return m;
  }, [photos, result]);
  const range = allPages ? null : { from: Math.min(from, to), to: Math.max(from, to) };
  const ids = useMemo(() => captionPhotoIds(pages as never, range), [pages, allPages, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const rangePages = useMemo(() => pages.map((p) => Number(p.page)).filter((n) => !range || (n >= range.from && n <= range.to)), [pages, allPages, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const targets = useMemo(
    () => captionTargets(pages, ids, langs.length ? langs : ['fr'], { replaceHandText: replaceHand, themedFree, fontFamily: font || null }),
    [pages, ids, langs, replaceHand, themedFree, font],
  );
  const places = useMemo(() => placeHolderCount(pages, rangePages), [pages, rangePages]);
  const pageGroups = useMemo(() => {
    const byPage = new Map<number, string[]>();
    for (const id of ids) {
      const p = pages.find((x) => (x.photos ?? []).includes(id));
      if (!p) continue;
      const n = Number(p.page);
      byPage.set(n, [...(byPage.get(n) ?? []), id]);
    }
    return [...byPage.entries()].map(([page, list]) => {
      const pg = pages.find((x) => Number(x.page) === page);
      return { page, ids: list, themed: Boolean(pg?.theme), model: String(pg?.model ?? ''), boxes: pg ? pageCaptionBoxes(pg) : [] as PageCaptionBox[] };
    });
  }, [ids, pages]);

  // The model's own caption face: what « Police du modèle » means here.
  const modelFamily = useMemo(() => {
    const at = pages.findIndex((p) => p.theme && rangePages.includes(Number(p.page)));
    return freeLook(pages, at >= 0 ? at : Math.max(0, pages.findIndex((p) => p.theme))).fontFamily;
  }, [pages, rangePages]);
  // The fonts are drawn with the album's own words when there are some.
  const sample = useMemo(() => {
    const first = [...Object.values(pageTexts), ...Object.values(texts)].map((t) => String(t ?? '').split('\n')[0].trim()).find(Boolean);
    return (first || factsText.split(/[,;\n]/)[0]?.trim() || 'Nos plus beaux souvenirs').slice(0, 42);
  }, [pageTexts, texts, factsText]);

  // The light under each caption: a 32×32 luminance grid of the photo's small copy.
  useEffect(() => {
    if (!result) return;
    let cancelled = false;
    for (const c of result.captions) {
      if (grids[c.photo]) continue;
      const p = photoById.get(c.photo);
      const url = p?.thumbnail_signed_url ?? p?.signed_url;
      if (!url) continue;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (cancelled) return;
        const g = lumaGridOf(img);
        if (g) setGrids((prev) => ({ ...prev, [c.photo]: g }));
      };
      img.src = url;
    }
    return () => { cancelled = true; };
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  // The seconds the model has been working.
  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(() => setElapsed(Math.round((Date.now() - busy.at) / 1000)), 500);
    return () => window.clearInterval(t);
  }, [busy]);

  // Escape closes (the font list closes first — it stops its own Escape).
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, busy, onClose]);

  const preview = useMemo(() => draftToAlbum(pages, { scope: ids, texts, before, removedPages: removed, placeLine, pageMode, pageTexts }, meta, {
    gapMm, luma: samplerFrom(grids), replaceHandText: replaceHand, style: band ? bandStyle : null,
    themedFree, fontFamily: font || null, fontColor: ink || null, placePages: rangePages,
  }), [pages, ids, texts, before, removed, placeLine, pageMode, pageTexts, meta, gapMm, grids, replaceHand, band, bandStyle, themedFree, font, ink, rangePages]);

  if (!open) return null;

  const toggleLang = (code: CaptionLangCode) =>
    setLangs((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : prev.length >= 3 ? prev : [...prev, code]));
  const inPageMode = (n: number) => Object.prototype.hasOwnProperty.call(pageMode, n);

  /** One model call per kind for these pages (all of the range, or one page — « Refaire »). */
  const generate = async (page: number | null) => {
    const groups = page == null ? pageGroups : pageGroups.filter((g) => g.page === page);
    const facts = factsText.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean).slice(0, 6);
    // Photo by photo: only photos with a place for words are sent, each with its box's length.
    const send = groups.filter((g) => !inPageMode(g.page)).flatMap((g) => g.ids).filter((id) => targets[id] && targets[id].kind !== 'none');
    const limits: Record<string, number> = {};
    for (const id of send) {
      const t = targets[id];
      if (t?.kind === 'holder' || (t?.kind === 'free' && t.where)) limits[id] = t.maxChars;
    }
    // A page at once: its box's length (or its first photo's, when the page has no box).
    const pageGroupsSend: Array<{ page: number; photo_ids: string[]; max_chars?: number }> = [];
    for (const g of groups.filter((x) => inPageMode(x.page))) {
      const box = pageMode[g.page];
      const pg = pages.find((x) => Number(x.page) === g.page);
      let max: number | undefined;
      if (box && pg) max = pageCaptionCapacity(pg, box, langs, font || null)?.maxChars;
      else { const t = targets[g.ids[0]]; if (t && t.kind !== 'none') max = t.maxChars; }
      if (box && !max) continue;
      pageGroupsSend.push({ page: g.page, photo_ids: g.ids, ...(max ? { max_chars: max } : {}) });
    }
    if (!send.length && !pageGroupsSend.length) {
      setError(page == null
        ? (themedFree
          ? 'Aucune place pour une légende sur ces pages (cadres déjà écrits à la main, ou pas de place dans la zone de sécurité).'
          : 'Aucune photo de ces pages n’a de cadre de texte dans son modèle — activez « Légender les photos sans cadre ».')
        : `Aucune place pour une légende sur la page ${page}.`);
      return;
    }
    setElapsed(0); setBusy({ pages: groups.map((g) => g.page), all: page == null, at: Date.now() }); setError(null);
    try {
      const [photoRes, pageRes] = await Promise.all([
        send.length ? captionsApi.preview({ session_id: sessionId, languages: langs, facts, photo_ids: send, quote, limits, instructions: wishes.trim() || undefined }) : Promise.resolve(null),
        pageGroupsSend.length ? captionsApi.preview({ session_id: sessionId, languages: langs, facts, quote, page_groups: pageGroupsSend, instructions: wishes.trim() || undefined }) : Promise.resolve(null),
      ]);
      if (photoRes) {
        const sent = new Set(send);
        setResult((prev) => (prev ? {
          ...photoRes,
          captions: [...prev.captions.filter((c) => !sent.has(c.photo)), ...photoRes.captions],
          failed: [...prev.failed.filter((f) => !sent.has(f.photo)), ...photoRes.failed],
          meta: { ...prev.meta, ...photoRes.meta },
        } : photoRes));
        // New words replace the old; a photo the model could not caption keeps what it had.
        setTexts((prev) => ({ ...prev, ...Object.fromEntries(photoRes.captions.map((c) => [c.photo, c.text])) }));
      }
      if (pageRes) {
        setPageTexts((prev) => ({ ...prev, ...Object.fromEntries((pageRes.pageCaptions ?? []).map((c) => [c.page, c.text])) }));
        setPageFailed((prev) => {
          const next = { ...prev };
          for (const g of pageGroupsSend) delete next[g.page];
          for (const f of pageRes.pageFailed ?? []) next[f.page] = f.why;
          return next;
        });
      }
      setRan(true);
      const done = new Set(groups.map((g) => g.page));
      setRemoved((prev) => prev.filter((n) => !done.has(n)));
    } catch (e) {
      setError((e as Error).message || 'Erreur inconnue');
    } finally {
      setBusy(null);
    }
  };

  const wordsOn = (g: { page: number; ids: string[] }) => g.ids.some((id) => String(texts[id] ?? '').trim() || String(before[id] ?? '').trim())
    || Boolean(String(pageTexts[g.page] ?? '').trim() || pageBefore[g.page])
    || Boolean(pages.find((x) => Number(x.page) === g.page)?.elements?.some((e) => e && e.holder === 'lieu_date' && e.captionPlace));

  /** « Retirer » a page: its words come off at « Appliquer »; again = undo. */
  const takeOff = (gs: Array<{ page: number; ids: string[] }>) => {
    setStash((prev) => ({ ...prev, ...Object.fromEntries(gs.map((g) => [g.page, { texts: Object.fromEntries(g.ids.map((id) => [id, texts[id] ?? before[id] ?? ''])), page: pageTexts[g.page] ?? pageBefore[g.page]?.text ?? '' }])) }));
    setRemoved((prev) => [...new Set([...prev, ...gs.map((g) => g.page)])]);
    setTexts((prev) => ({ ...prev, ...Object.fromEntries(gs.flatMap((g) => g.ids.map((id) => [id, '']))) }));
    setPageTexts((prev) => ({ ...prev, ...Object.fromEntries(gs.map((g) => [g.page, ''])) }));
  };
  const putBack = (gs: Array<{ page: number; ids: string[] }>) => {
    const back = new Set(gs.map((g) => g.page));
    setRemoved((prev) => prev.filter((n) => !back.has(n)));
    setTexts((prev) => ({ ...prev, ...Object.fromEntries(gs.flatMap((g) => g.ids.map((id) => [id, stash[g.page]?.texts[id] ?? before[id] ?? '']))) }));
    setPageTexts((prev) => ({ ...prev, ...Object.fromEntries(gs.map((g) => [g.page, stash[g.page]?.page ?? pageBefore[g.page]?.text ?? ''])) }));
  };
  const toggleRemove = (page: number, list: string[]) => (removed.includes(page) ? putBack : takeOff)([{ page, ids: list }]);

  // « Tout retirer » — every page of the range that has words; again = put them all back.
  const removable = pageGroups.filter((g) => wordsOn(g) || removed.includes(g.page));
  const allRemoved = removable.length > 0 && removable.every((g) => removed.includes(g.page));
  const toggleRemoveAll = () => (allRemoved ? putBack(removable) : takeOff(removable.filter((g) => !removed.includes(g.page))));

  // « Une légende par page » for every page of two photos or more.
  const multi = pageGroups.filter((g) => g.ids.length > 1);
  const allPageMode = multi.length > 0 && multi.every((g) => inPageMode(g.page));
  const setModeFor = (list: typeof pageGroups, on: boolean) => setPageMode((prev) => {
    const next = { ...prev };
    for (const g of list) {
      if (on) { if (!(g.page in next)) next[g.page] = g.boxes[0]?.elementId ?? null; }
      else delete next[g.page];
    }
    return next;
  });

  // What the model wrote (photo by photo and page by page) — the line above the pages.
  const written = (result?.captions.length ?? 0) + Object.values(pageTexts).filter((t) => String(t ?? '').trim()).length;
  const unwritten = (result?.failed.length ?? 0) + Object.keys(pageFailed).length;
  const failedWhy = new Map((result?.failed ?? []).map((f) => [f.photo, f.why]));
  const placedBy = new Map(preview.placed.map((p) => [p.photoId, p]));
  const skippedBy = new Map(preview.skipped.map((s) => [s.photoId, s]));
  const pagePlacedBy = new Map(preview.pagePlaced.map((p) => [p.page, p]));
  const pageSkippedBy = new Map(preview.pageSkipped.map((p) => [p.page, p]));
  const placedCount = preview.placed.length + preview.pagePlaced.length;
  const changes = placedCount + preview.removed + preview.places;
  const applyLabel = [
    placedCount ? `${placedCount} légende${placedCount > 1 ? 's' : ''}` : '',
    preview.removed ? `${preview.removed} retirée${preview.removed > 1 ? 's' : ''}` : '',
    preview.places ? `${preview.places} « Lieu · date »` : '',
  ].filter(Boolean).join(', ');
  const field = { border: `1px solid ${T.line}`, background: T.panel, color: T.ink };
  const writeFont = font || modelFamily;
  const busyPhotos = busy ? pageGroups.filter((g) => busy.pages.includes(g.page)).reduce((n, g) => n + g.ids.length, 0) : 0;
  const thumb = (id: string, cls: string) => {
    const p = photoById.get(id);
    const url = p?.thumbnail_signed_url ?? p?.signed_url;
    return url ? <img src={url} alt="" className={`${cls} object-cover rounded-md`} /> : <div className={`${cls} rounded-md`} style={{ background: T.chip }} />;
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Légendes IA"
      className="fixed inset-0 z-[2000] flex items-center justify-center p-0 sm:p-3"
      style={{ background: 'rgba(17,24,39,0.6)' }}
      onClick={() => { if (!busy) onClose(); }}
    >
      <style>{MOTION_CSS}</style>
      <div
        // overflow CLIP, not hidden: a hidden box can still be scrolled by a focus,
        // a clipped one never — the bar stays at the bottom whatever gets focus.
        className="relative w-full max-w-6xl h-full sm:h-[94vh] flex flex-col sm:rounded-2xl shadow-2xl overflow-hidden [overflow:clip]"
        style={{ background: T.surface, color: T.ink }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — and, while the model works, the travelling bar under it. */}
        <header className="relative px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${T.line}` }}>
          <div className="min-w-0">
            <h2 className="text-lg font-bold leading-tight">✍️ Légendes IA</h2>
            <p className="text-xs mt-0.5 truncate" style={{ color: T.muted }}>
              L’IA regarde les photos et écrit une ligne par langue. Rien n’est enregistré avant « Appliquer ».
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={Boolean(busy)} className="shrink-0 w-9 h-9 rounded-full text-base disabled:opacity-40" style={{ color: T.muted, background: T.chip }} aria-label="Fermer">✕</button>
          {busy && (
            <div className="absolute left-0 right-0 bottom-0 h-[3px] overflow-hidden" aria-hidden>
              <div className="cap-bar h-full w-2/5 rounded-full" style={{ background: GOLD }} />
            </div>
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row">
          {/* ── Settings ─────────────────────────────────────────────── */}
          <aside className="md:w-[340px] md:shrink-0 md:overflow-y-auto px-4 sm:px-5 py-4 space-y-5" style={{ borderRight: `1px solid ${T.line}`, background: T.panel }}>
            <Section title="Langues du client" hint="dans l’ordre d’affichage, 3 max">
              <div className="flex flex-wrap gap-1.5">
                {LANGS.map((l) => {
                  const at = langs.indexOf(l.code);
                  return (
                    <button
                      key={l.code} type="button" onClick={() => toggleLang(l.code)} aria-pressed={at >= 0}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ border: `1px solid ${at >= 0 ? GOLD : T.line}`, background: at >= 0 ? `${GOLD}22` : T.chip, color: at >= 0 ? T.goldInk : T.ink }}
                    >
                      {at >= 0 && <span className="inline-flex items-center justify-center w-4 h-4 mr-1 rounded-full text-[10px] text-white" style={{ background: GOLD }}>{at + 1}</span>}
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </Section>

            <Section title="Pages" hint={`${ids.length} photo${ids.length > 1 ? 's' : ''}`}>
              <div className="flex rounded-lg p-0.5 text-xs font-semibold" style={{ background: T.chip }}>
                {[{ v: true, l: 'Tout l’album' }, { v: false, l: 'Quelques pages' }].map((o) => (
                  <button key={String(o.v)} type="button" onClick={() => setAllPages(o.v)} aria-pressed={allPages === o.v}
                    className="flex-1 py-1.5 rounded-md" style={{ background: allPages === o.v ? T.surface : 'transparent', color: allPages === o.v ? T.ink : T.muted }}>
                    {o.l}
                  </button>
                ))}
              </div>
              {!allPages && (
                <div className="flex items-center gap-2 text-xs">
                  de <input type="number" min={1} max={pages.length} value={from} onChange={(e) => setFrom(Number(e.target.value) || 1)} className="w-16 px-2 py-1 rounded-md" style={field} aria-label="Première page" />
                  à <input type="number" min={1} max={pages.length} value={to} onChange={(e) => setTo(Number(e.target.value) || 1)} className="w-16 px-2 py-1 rounded-md" style={field} aria-label="Dernière page" />
                </div>
              )}
            </Section>

            <Section title="Ce que le client demande" hint="l’IA suit ces consignes">
              <textarea
                value={wishes} onChange={(e) => setWishes(e.target.value)} rows={5} maxLength={1500} dir="auto"
                placeholder={'Écrivez comme vous voulez, ex.\n« Joyeux anniversaire Ziyad » sur la photo du gâteau.\nLa date 12/03/2024 sur la première page.\nUne citation sur l’amitié pour les photos avec ses cousins.'}
                className="w-full px-3 py-2 rounded-lg text-sm leading-snug resize-y" style={{ ...field, unicodeBidi: 'plaintext' }}
                aria-label="Consignes du client pour les légendes"
              />
              <p className="text-[11px] leading-snug" style={{ color: T.muted }}>
                Les mots entre « guillemets » sont recopiés tels quels. Les noms, dates et lieux écrits ici sont autorisés — l’IA n’en invente aucun autre.
                {wishes.length > 1200 ? ` ${1500 - wishes.length} caractères restants.` : ''}
              </p>
            </Section>

            <Section title="Prénoms, dates" hint="facultatif">
              <input type="text" value={factsText} onChange={(e) => setFactsText(e.target.value)} placeholder="ex. Najat, 07/10/2020"
                className="w-full px-3 py-2 rounded-lg text-sm" style={field} />
            </Section>

            {places > 0 && (
              <Section title="Lieu · date" hint={`${places} cadre${places > 1 ? 's' : ''} vide${places > 1 ? 's' : ''}`}>
                <input type="text" value={placeLine} onChange={(e) => setPlaceLine(e.target.value)} placeholder="ex. Dubai & Maldives · 2026" maxLength={40}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={field} />
              </Section>
            )}

            <Section title="Police">
              <CaptionFontPicker
                value={font} onChange={setFont} modelFamily={modelFamily} sample={sample}
                tokens={{ ink: T.ink, muted: T.muted, panel: T.surface, line: T.line, chip: T.chip, gold: GOLD, goldInk: T.goldInk }}
              />
            </Section>

            <Section title="Couleur">
              <div className="flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => setInk('')} aria-pressed={!ink}
                  className="px-2.5 h-7 rounded-full text-[11px] font-semibold"
                  style={{ border: `1.5px solid ${!ink ? GOLD : T.line}`, background: T.chip, color: !ink ? T.goldInk : T.ink }}>
                  Couleur du modèle
                </button>
                {INKS.map((c) => (
                  <button key={c} type="button" onClick={() => setInk(c)} aria-label={`Couleur ${c}`} aria-pressed={ink === c}
                    className="w-7 h-7 rounded-full" style={{ background: c, border: ink === c ? `2.5px solid ${GOLD}` : `1px solid ${T.line}` }} />
                ))}
                <label className="relative w-7 h-7 rounded-full overflow-hidden cursor-pointer" title="Autre couleur"
                  style={{ border: ink && !INKS.includes(ink) ? `2.5px solid ${GOLD}` : `1px solid ${T.line}`, background: 'conic-gradient(#e11d48, #f59e0b, #22c55e, #3b82f6, #a855f7, #e11d48)' }}>
                  <input type="color" value={ink || '#1F1A1C'} onChange={(e) => setInk(e.target.value.toUpperCase())} className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Autre couleur" />
                </label>
              </div>
            </Section>

            <Section title="Options">
              <div className="space-y-3">
                <Toggle on={quote} onChange={setQuote} label="Ajouter une citation" help="Une courte حكمة après chaque légende — jamais un verset, pas dans les petits cadres." />
                <Toggle on={themedFree} onChange={setThemedFree} label="Légender les photos sans cadre" help="Pages à modèle : un texte libre se pose à côté de la photo, dans le style du modèle, loin des bords." />
                <Toggle on={band} onChange={setBand} label="Cadre de la couleur du livre" help="Pour les légendes posées sur la photo (comme le livre de Roaa) ; sinon, mots avec un contour.">
                  <span aria-hidden className="inline-block w-5 h-3 rounded-sm" style={{ background: bandStyle.band, border: `1px solid ${bandStyle.ink}` }} />
                </Toggle>
                <Toggle on={replaceHand} onChange={setReplaceHand} label="Remplacer les textes déjà écrits" help="Sinon la légende se place à côté, jamais dessus." />
              </div>
            </Section>
          </aside>

          {/* ── Pages ─────────────────────────────────────────────────── */}
          <main className="md:flex-1 min-w-0 flex flex-col md:min-h-0">
            <div className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2" style={{ borderBottom: `1px solid ${T.line}` }}>
              <button
                type="button" onClick={() => generate(null)} disabled={busy != null || !langs.length || !ids.length}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                style={{ background: GOLD_BTN }}
              >
                {busy?.all ? <><Spinner /> L’IA regarde {busyPhotos} photo{busyPhotos > 1 ? 's' : ''}… {elapsed} s</>
                  : result || Object.keys(pageTexts).length ? '↻ Tout régénérer' : 'Générer les légendes'}
              </button>
              {busy?.all && <span className="text-xs" style={{ color: T.muted }}>≈ 1 min pour une cinquantaine de photos</span>}
              {!busy && !langs.length && <span className="text-xs" style={{ color: T.muted }}>Choisissez d’abord une langue.</span>}
              <div className="flex-1" />
              {multi.length > 0 && (
                <Toggle on={allPageMode} onChange={(v) => setModeFor(multi, v)} label="Une légende par page" />
              )}
              <button
                type="button" onClick={toggleRemoveAll} disabled={busy != null || !removable.length}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                style={{ border: `1px solid ${allRemoved ? T.line : T.danger}`, color: allRemoved ? T.ink : T.danger }}
                title="Enlever toutes les légendes de ces pages (les cadres du modèle reprennent leur texte)"
              >
                {allRemoved ? 'Tout remettre' : 'Tout retirer'}
              </button>
            </div>

            {(error || ran) && (
              <div className="px-4 sm:px-5 pt-3 text-xs space-y-1">
                {error && <div className="px-3 py-2 rounded-lg" style={{ color: T.danger, background: `${GOLD}10`, border: `1px solid ${T.danger}` }}>{error}</div>}
                {ran && !error && (
                  <div style={{ color: T.muted }}>
                    {written} légende{written > 1 ? 's' : ''} écrite{written > 1 ? 's' : ''}
                    {unwritten ? ` · ${unwritten} sans légende (raison sous la photo)` : ''} · chaque texte se modifie avant d’appliquer.
                  </div>
                )}
              </div>
            )}

            <div className="md:flex-1 md:overflow-y-auto px-4 sm:px-5 py-3 space-y-3">
              {!pageGroups.length && (
                <div className="text-sm py-10 text-center" style={{ color: T.muted }}>Aucune photo sur ces pages.</div>
              )}
              {pageGroups.map((g) => {
                const isRemoved = removed.includes(g.page);
                const writing = Boolean(busy?.pages.includes(g.page));
                const paged = inPageMode(g.page);
                const box = paged ? pageMode[g.page] : null;
                const boxInfo = g.boxes.find((b) => b.elementId === box);
                const ptxt = pageTexts[g.page] ?? '';
                let pageStatus = '';
                let pageBad = false;
                if (paged) {
                  const pp = pagePlacedBy.get(g.page), ps = pageSkippedBy.get(g.page), pf = pageFailed[g.page];
                  if (box) {
                    if (pp) pageStatus = `✓ remplace « ${PAGE_ROLE_FR[pp.role] ?? pp.role} »${boxInfo?.origText ? ` (« ${boxInfo.origText.slice(0, 40)} »)` : ''}`;
                    else if (ps) { pageStatus = `✕ ${frenchReason('too_long', ps.why)}`; pageBad = true; }
                    else if (pf && !ptxt.trim()) { pageStatus = `✕ ${frenchReason('model', pf)}`; pageBad = true; }
                    else if (ptxt.trim() && pageBefore[g.page]?.text === ptxt) pageStatus = 'Déjà dans l’album';
                    else {
                      const pg = pages.find((x) => Number(x.page) === g.page);
                      const cap = pg ? pageCaptionCapacity(pg, box, langs.length ? langs : ['fr'], font || null) : null;
                      pageStatus = cap ? `→ remplace le cadre « ${boxInfo?.label} » · ${cap.maxChars} car. max par langue` : `✕ Le cadre « ${boxInfo?.label} » est trop petit pour ${langs.length || 1} langue${langs.length > 1 ? 's' : ''}.`;
                      pageBad = !cap;
                    }
                  } else {
                    const fp = placedBy.get(g.ids[0]);
                    pageStatus = fp ? '✓ avec la première photo' : pageFailed[g.page] && !ptxt.trim() ? `✕ ${frenchReason('model', pageFailed[g.page])}` : `${targetHint(targets[g.ids[0]], g.themed)} (première photo — la page n’a pas de sous-titre ni de section)`;
                  }
                }
                return (
                  <article key={g.page} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${writing ? GOLD : T.line}`, opacity: isRemoved ? 0.6 : 1 }}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2" style={{ background: T.chip, borderBottom: `1px solid ${T.line}` }}>
                      <div className="text-sm font-semibold">
                        Page {g.page}
                        <span className="ml-1.5 font-normal text-xs" style={{ color: T.muted }}>
                          {g.themed ? `modèle ${g.model}, ` : ''}{g.ids.length} photo{g.ids.length > 1 ? 's' : ''}{isRemoved ? ' — retirées à « Appliquer »' : ''}
                        </span>
                      </div>
                      <div className="flex-1" />
                      {g.ids.length > 1 && (
                        <div className="flex rounded-lg p-0.5 text-[11px] font-semibold" style={{ background: T.surface }} role="group" aria-label={`Légendes de la page ${g.page}`}>
                          {[{ v: false, l: 'Par photo' }, { v: true, l: 'Une pour la page' }].map((o) => (
                            <button key={String(o.v)} type="button" onClick={() => setModeFor([g], o.v)} aria-pressed={paged === o.v} disabled={busy != null}
                              className="px-2.5 py-1 rounded-md" style={{ background: paged === o.v ? `${GOLD}2A` : 'transparent', color: paged === o.v ? T.goldInk : T.muted }}>
                              {o.l}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        type="button" onClick={() => generate(g.page)} disabled={busy != null || !langs.length}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold disabled:opacity-50"
                        style={{ border: `1px solid ${GOLD}`, color: T.goldInk }}
                        title="Réécrire les légendes de cette page seulement"
                      >
                        {writing && !busy?.all ? <><Spinner size={12} /> {elapsed} s</> : '↻ Refaire'}
                      </button>
                      <button
                        type="button" onClick={() => toggleRemove(g.page, g.ids)} disabled={busy != null || (!wordsOn(g) && !isRemoved)}
                        className="px-2.5 py-1 rounded-md text-[11px] font-semibold disabled:opacity-40"
                        style={{ border: `1px solid ${T.line}`, color: isRemoved ? T.ink : T.danger }}
                        title="Enlever les légendes de cette page"
                      >
                        {isRemoved ? 'Remettre' : 'Retirer'}
                      </button>
                    </div>

                    {paged ? (
                      <div className="p-3 space-y-2">
                        <div className="flex gap-1.5 overflow-x-auto pb-0.5">{g.ids.map((id) => <span key={id}>{thumb(id, 'w-16 h-16 shrink-0')}</span>)}</div>
                        {g.boxes.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                            <span style={{ color: T.muted }}>Écrire dans :</span>
                            {g.boxes.map((b) => (
                              <button key={b.elementId} type="button" onClick={() => setPageMode((prev) => ({ ...prev, [g.page]: b.elementId }))} aria-pressed={box === b.elementId}
                                className="px-2.5 py-1 rounded-full max-w-full truncate"
                                style={{ border: `1px solid ${box === b.elementId ? GOLD : T.line}`, background: box === b.elementId ? `${GOLD}22` : 'transparent', color: box === b.elementId ? T.goldInk : T.ink }}
                                title={b.origText ? `Remplace « ${b.origText} »` : undefined}>
                                {b.label}{b.origText ? ` · « ${b.origText.slice(0, 24)}${b.origText.length > 24 ? '…' : ''} »` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className={`rounded-lg ${writing ? 'cap-shimmer' : ''}`}>
                          <textarea
                            value={ptxt} rows={Math.max(2, ptxt.split('\n').length)} dir="auto" disabled={writing}
                            onChange={(e) => setPageTexts((prev) => ({ ...prev, [g.page]: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg text-base resize-y"
                            style={{ ...field, background: writing ? 'transparent' : T.panel, unicodeBidi: 'plaintext', fontFamily: writeFont }}
                            placeholder={writing ? 'L’IA regarde les photos de la page…' : 'Une légende pour toute la page — « ↻ Refaire » pour la faire écrire'}
                            aria-label={`Légende de la page ${g.page}`}
                          />
                        </div>
                        {pageStatus && <div className="text-[11px]" style={{ color: pageBad ? T.danger : T.muted }}>{pageStatus}</div>}
                      </div>
                    ) : (
                      <div className="p-3 space-y-3">
                        {g.ids.map((id) => {
                          const t = targets[id];
                          const placed = placedBy.get(id);
                          const skipped = skippedBy.get(id);
                          const why = failedWhy.get(id);
                          const txt = texts[id] ?? '';
                          const none = t?.kind === 'none' && !txt.trim();
                          let status: string;
                          let bad = false;
                          if (placed) {
                            status = placed.holder ? `✓ dans le cadre « ${ROLE_FR[placed.holder] ?? placed.holder} » du modèle`
                              : placed.spot && placed.spot !== 'photo' ? `✓ texte libre ${SPOT_FR[placed.spot]}`
                                : `✓ posée en ${placed.band === 'top' ? 'haut' : 'bas'} de la photo${placed.spot === 'photo' ? ' (pas de place autour)' : ''}`;
                          }
                          else if (skipped) { status = `✕ ${frenchReason(skipped.reason, skipped.why)}`; bad = skipped.reason !== 'no_holder'; }
                          else if (why && !txt.trim()) { status = `✕ ${frenchReason('model', why)}`; bad = true; }
                          else if (txt.trim() && txt.trim() === String(before[id] ?? '').trim()) status = `Déjà dans l’album · ${targetHint(t, g.themed)}`;
                          else status = targetHint(t, g.themed) || 'Aucune légende';
                          // A photo the model gave no text box is the design, not a failure: said quietly.
                          if (t?.kind === 'none' && !placed && t.reason !== 'no_holder') bad = true;
                          return (
                            <div key={id} className="flex gap-3 items-start">
                              {thumb(id, 'w-14 h-14 shrink-0')}
                              <div className="flex-1 min-w-0">
                                {none ? null : (
                                  <div className={`rounded-lg ${writing ? 'cap-shimmer' : ''}`}>
                                    <textarea
                                      value={txt} rows={Math.max(2, txt.split('\n').length)} dir="auto" disabled={writing}
                                      onChange={(e) => setTexts((prev) => ({ ...prev, [id]: e.target.value }))}
                                      className="w-full px-3 py-1.5 rounded-lg text-[15px] resize-y"
                                      style={{ ...field, background: writing ? 'transparent' : T.panel, unicodeBidi: 'plaintext', fontFamily: writeFont }}
                                      placeholder={writing ? 'L’IA regarde la photo…' : 'Pas de légende pour cette photo'}
                                    />
                                  </div>
                                )}
                                <div className="text-[11px] mt-0.5" style={{ color: bad ? T.danger : T.muted }}>{status}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </main>
        </div>

        <footer className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3" style={{ borderTop: `1px solid ${T.line}` }}>
          <div className="hidden sm:block text-[11px] min-w-0" style={{ color: T.muted }}>
            Écrit en <span style={{ fontFamily: writeFont, color: T.ink }}>{font ? fontName(font) : `${fontName(modelFamily)} (modèle)`}</span>, {ink ? 'votre couleur' : 'couleur du modèle'} · jamais sur un visage ni près des bords.
          </div>
          <div className="flex gap-2 justify-end min-w-0">
            <button type="button" onClick={onClose} disabled={Boolean(busy)} className="shrink-0 px-4 py-2 rounded-lg text-sm whitespace-nowrap disabled:opacity-50" style={{ background: T.chip, color: T.ink }}>Annuler</button>
            <button
              type="button" disabled={busy != null || !changes}
              onClick={() => { onApply(preview.pages, { placed: placedCount, skipped: preview.skipped.length + preview.pageSkipped.length, removed: preview.removed }); onClose(); }}
              className="min-w-0 px-5 py-2 rounded-lg text-sm font-bold text-white leading-tight sm:whitespace-nowrap disabled:opacity-50"
              style={{ background: GOLD_BTN }}
            >
              Appliquer{applyLabel ? ` · ${applyLabel}` : ''}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
